import { useAuthStore } from "@/lib/auth/AuthStore";
import * as Sentry from "@sentry/nextjs";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useVariantKeyManager } from "./useVariantKeyManager";
// production 빌드는 console.* 가 strip되므로 Toss 위젯 라이프사이클 실패는
// Sentry로 분류 보고한다. fingerprint로 noise 그룹과 분리 → 실제 결제 위젯 장애 추적용
const reportTossWidgetError = (phase, error)=>{
    ;
    Sentry.captureException(error, {
        tags: {
            feature: "toss-widget",
            phase
        },
        fingerprint: [
            "toss-widget-".concat(phase)
        ]
    });
};
/** 금액 반영 실패로 위젯을 다시 띄우는 횟수 상한. 넘으면 무한 반복이 되므로 멈춘다. */ const MAX_AMOUNT_SYNC_RETRY = 2;
/**
 * 토스 결제위젯을 관리하는 훅
 *
 * 결제 페이지에서 실제로 일어나는 순서 (→ 는 아래 주석 번호):
 *
 *   결제할 금액이 정해진다        캐시·포인트를 한 글자 칠 때마다 바뀐다
 *        ↓
 *   결제수단 조합이 정해진다       10,000원을 넘으면 가상계좌가 붙는다        → 1
 *        ↓
 *   토스 SDK를 불러온다           로그인한 사용자당 한 번                    → 2
 *        ↓
 *   "이걸 띄워라" 지시서를 만든다   PaymentWidget에 넘긴다                    → 7
 *        ↓
 *   PaymentWidget이 실제로 띄운다  띄우고 지우는 일은 저쪽 담당               → 5
 *        ↓
 *   다 떴다고 알려온다            여기에 보관한다                           → 3
 *        ↓
 *   결제 버튼이 열린다            화면 금액과 위젯 금액이 같을 때만           → 8
 *
 * 금액이 10,000원 경계를 넘으면 지시서의 key가 바뀌고, React가 헌 위젯을 지운 뒤
 * 새로 띄운다. 경계를 넘지 않는 금액 변화는 위젯을 그대로 두고 금액만 갱신한다.
 */ const usePgBridge = (param)=>{
    let { orderType , amount , isPassAmountType , packageVariantKey  } = param;
    const { user  } = useAuthStore();
    const customerKey = (user === null || user === void 0 ? void 0 : user.id) ? String(user.id) : null;
    // 1. 금액에서 결제수단 조합을 정한다. 금액과 같은 렌더에서 계산되므로 둘이 어긋나지 않는다.
    const { variantKey  } = useVariantKeyManager({
        orderType,
        amount,
        isPassAmountType,
        packageVariantKey
    });
    // 2. 토스 SDK를 불러온다. 로그인한 사용자가 바뀔 때만 다시 만든다.
    const [widgets, setWidgets] = useState(null);
    useEffect(()=>{
        if (!customerKey) {
            setWidgets(null);
            return;
        }
        let cancelled = false;
        loadTossPayments(process.env.NEXT_PUBLIC_TOSS_PG).then((tossPayments)=>{
            if (cancelled) return;
            setWidgets(tossPayments.widgets({
                customerKey
            }));
        }).catch((error)=>reportTossWidgetError("load", error));
        return ()=>{
            cancelled = true;
        };
    }, [
        customerKey
    ]);
    // 3. 지금 화면에 떠 있는 결제 UI. 없으면 결제 버튼이 잠긴다.
    // state와 ref를 같이 두는 이유 — 버튼을 잠그려면 리렌더가 필요하고(state),
    // 결제 직전 검증은 리렌더를 기다릴 수 없어 최신값을 바로 읽어야 한다(ref).
    const [paymentUi, setPaymentUi] = useState(null);
    const paymentUiRef = useRef(null);
    // 4. 위젯을 통째로 다시 띄워야 할 때 올린다. 아래 두 경우뿐이다.
    //  - 다른 탭에 갔다 돌아왔을 때: 위젯이 멎어 있다
    //  - 금액을 위젯에 반영하지 못했을 때: 위젯이 든 금액을 믿을 수 없다
    const [resetGen, setResetGen] = useState(0);
    const amountSyncRetryRef = useRef(0);
    // 위젯이 다 떴다 → 결제 버튼을 연다
    const handlePaymentUiReady = useCallback((next)=>{
        paymentUiRef.current = next;
        setPaymentUi(next);
        amountSyncRetryRef.current = 0;
    }, []);
    // 위젯에 새 금액이 반영됐다
    const handleAmountApplied = useCallback((renderId, nextAmount)=>{
        var _paymentUiRef_current;
        // 현재 결제 UI의 보고만 반영한다
        if (((_paymentUiRef_current = paymentUiRef.current) === null || _paymentUiRef_current === void 0 ? void 0 : _paymentUiRef_current.renderId) !== renderId) return;
        paymentUiRef.current = {
            ...paymentUiRef.current,
            amount: nextAmount
        };
        setPaymentUi(paymentUiRef.current);
    }, []);
    // 위젯이 사라졌다 → 결제 버튼을 잠근다
    const handlePaymentUiDestroyed = useCallback((renderId)=>{
        var _paymentUiRef_current;
        // 현재 결제 UI의 해제만 반영한다
        if (((_paymentUiRef_current = paymentUiRef.current) === null || _paymentUiRef_current === void 0 ? void 0 : _paymentUiRef_current.renderId) !== renderId) return;
        paymentUiRef.current = null;
        setPaymentUi(null);
    }, []);
    // 금액을 위젯에 반영하지 못했다 → 위젯을 다시 띄워 복구를 시도한다
    const handleAmountSyncFailed = useCallback(()=>{
        if (amountSyncRetryRef.current >= MAX_AMOUNT_SYNC_RETRY) {
            // 다시 띄워도 계속 실패한다. 더 반복하면 무한 루프가 되므로 멈춘다.
            // 위젯이 든 금액이 화면과 다른 채로 남으므로 결제 버튼은 잠긴다.
            reportTossWidgetError("set-amount-exhausted", new Error("결제 금액 동기화가 ".concat(MAX_AMOUNT_SYNC_RETRY, "회 연속 실패했습니다")));
            return;
        }
        amountSyncRetryRef.current += 1;
        setResetGen((generation)=>generation + 1);
    }, []);
    // 5. 헌 위젯을 지우는 중인 작업. 새 위젯은 이게 끝나야 뜬다.
    // 토스가 한 페이지에 결제 UI를 하나만 허용하기 때문이다.
    // 헌 위젯은 사라지면서 자기 정보를 잃으므로, 이 약속은 부모인 이 훅이 들고 있어야 한다.
    const pendingDestroyRef = useRef(Promise.resolve());
    // 6. 다른 탭에 갔다 돌아오면 토스 위젯이 멎어 있어서 다시 띄운다
    useEffect(()=>{
        let wasHidden = false;
        const handleVisibilityChange = ()=>{
            if (document.visibilityState === "hidden") {
                wasHidden = true;
                return;
            }
            if (document.visibilityState !== "visible" || !wasHidden) return;
            wasHidden = false;
            // 떠 있는 위젯이 있을 때만. 없으면 괜히 다시 띄우는 셈이 된다.
            if (paymentUiRef.current) {
                setResetGen((generation)=>generation + 1);
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return ()=>{
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);
    // 7. PaymentWidget에 넘길 지시서. 위젯을 띄우면 안 되는 경우는 전부 null로 모인다
    // (0원 결제 / 로그인 안 됨 / 주문 종류를 몰라 결제수단을 못 정함).
    // key가 바뀌면 React가 헌 위젯을 지우고 새로 띄운다.
    const isValidVariantKey = !!variantKey && variantKey !== "DEFAULT";
    const mountKey = "".concat(variantKey, ":").concat(resetGen);
    const mount = useMemo(()=>{
        if (!widgets || !isValidVariantKey || amount <= 0) return null;
        return {
            key: mountKey,
            props: {
                widgets,
                mountKey,
                variantKey,
                amount,
                pendingDestroyRef,
                onPaymentUiReady: handlePaymentUiReady,
                onAmountApplied: handleAmountApplied,
                onPaymentUiDestroyed: handlePaymentUiDestroyed,
                onError: reportTossWidgetError,
                onAmountSyncFailed: handleAmountSyncFailed
            }
        };
    }, [
        widgets,
        isValidVariantKey,
        amount,
        mountKey,
        variantKey,
        handlePaymentUiReady,
        handleAmountApplied,
        handlePaymentUiDestroyed,
        handleAmountSyncFailed
    ]);
    // 8. 결제 버튼을 열어도 되는가.
    // 방금 입력한 금액이 위젯에 아직 반영되지 않은 동안은 잠가둔다. 그 사이에 누르면 결제가 실패한다.
    const ready = !!paymentUi && paymentUi.variantKey === variantKey && paymentUi.amount === amount;
    /**
   * 결제 버튼을 눌렀을 때, 주문을 만들기 전에 부른다.
   *
   * 결제 흐름이 "주문 생성 → 쿠폰·캐시·포인트 반영 → 결제창"이라, 여기서 막지 않으면
   * 서버 작업을 다 해놓고 마지막에 결제창을 못 띄운다.
   * 검증을 통과한 결제 UI를 그대로 돌려주므로 결제 요청도 같은 것을 쓴다.
   */ const assertPayable = ()=>{
        if (amount <= 0) {
            throw new Error("결제 금액이 0원 이하입니다. 쿠폰 할인 금액을 확인해주세요.");
        }
        const current = paymentUiRef.current;
        if (!current) {
            throw new Error("결제위젯이 준비되지 않았습니다. 잠시 후 다시 시도해주세요.");
        }
        if (current.variantKey !== variantKey || current.amount !== amount) {
            throw new Error("결제 정보가 변경되었습니다. 금액을 확인하고 다시 시도해주세요.");
        }
        return current;
    };
    // 9. 토스 결제창을 띄운다
    const requestPayment = async (paymentRequest)=>{
        const payable = assertPayable();
        try {
            await payable.widgets.requestPayment(paymentRequest);
        } catch (error) {
            ;
            throw error;
        }
    };
    return {
        ready,
        mount,
        assertPayable,
        requestPayment
    };
};
export default usePgBridge;
